import { useEffect } from 'react';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
}

const BASE_URL = 'https://reviewplum.com';
const DEFAULT_TITLE = 'Review Plum - AI-Powered Google Review Generation for Local Businesses';
const DEFAULT_DESCRIPTION = 'Generate authentic, SEO-optimized review suggestions for your business. 100% Google-compliant. Turn happy customers into 5-star reviews.';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

export default function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper function to set or create meta tag
    const setMetaTag = (selector: string, content: string, attrName: 'name' | 'property' = 'name', attrValue?: string) => {
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue || '');
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // Set meta description
    setMetaTag('meta[name="description"]', description, 'name', 'description');

    // Set meta keywords
    if (keywords.length > 0) {
      setMetaTag('meta[name="keywords"]', keywords.join(', '), 'name', 'keywords');
    }

    // Set robots meta
    if (noIndex) {
      setMetaTag('meta[name="robots"]', 'noindex, nofollow', 'name', 'robots');
    }

    // Set canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonical) {
        canonical.setAttribute('href', canonicalUrl);
      } else {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('href', canonicalUrl);
        document.head.appendChild(canonical);
      }
    }

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:site_name', content: 'Review Plum' },
      { property: 'og:image', content: ogImage },
      ...(canonicalUrl ? [{ property: 'og:url', content: canonicalUrl }] : []),
    ];

    ogTags.forEach(({ property, content }) => {
      setMetaTag(`meta[property="${property}"]`, content, 'property', property);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ];

    twitterTags.forEach(({ name, content }) => {
      setMetaTag(`meta[name="${name}"]`, content, 'name', name);
    });

    // JSON-LD structured data
    if (jsonLd) {
      const existingScript = document.querySelector('script[data-seo-jsonld]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector('script[data-seo-jsonld]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, noIndex, jsonLd]);

  return null;
}

// Pre-configured SEO for common pages
export const HomeSEO = () => (
  <SEO
    title="Review Plum - AI-Powered Google Review Generation for Local Businesses in India"
    description="Generate authentic, SEO-optimized review suggestions for your business. 100% Google-compliant. Turn happy customers into 5-star reviews. Starts at ₹5,000/year."
    keywords={[
      'google reviews',
      'review generation',
      'local seo',
      'business reviews',
      'review management',
      'google business profile',
      'customer reviews india',
    ]}
    canonicalUrl="https://reviewplum.com"
    jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'Review Plum',
      'applicationCategory': 'BusinessApplication',
      'operatingSystem': 'Web',
      'offers': {
        '@type': 'Offer',
        'price': '5000',
        'priceCurrency': 'INR',
        'priceValidUntil': '2026-12-31',
      },
    }}
  />
);
